import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";
import TopNav from "@/components/TopNav";

export default function TeamUp() {
  const navigate = useNavigate();

  useEffect(() => {
    // Fire a celebratory confetti burst on mount.
    const end = Date.now() + 800;
    const colors = ["#d64533", "#F5B72C", "#ffffff"];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="team-up-page"
    >
      <TopNav />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-8 pb-32 flex flex-col items-center">
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full card-brut bg-white overflow-hidden"
          data-testid="team-up-card"
        >
          <div className="flex items-start gap-4 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)] bg-[var(--primary)]">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-white shadow-[3px_3px_0px_var(--border-heavy)]">
              <PartyPopper strokeWidth={2.5} className="h-6 w-6 text-[var(--text-main)]" />
            </span>
            <div className="pt-1">
              <p className="font-heading text-xs font-bold uppercase tracking-wider text-[var(--text-main)]/70">
                Build Team
              </p>
              <h1
                className="mt-0.5 font-heading text-2xl sm:text-3xl font-semibold leading-snug text-[var(--text-main)]"
                data-testid="team-up-title"
              >
                Your build is done!
              </h1>
            </div>
          </div>

          <div
            className="relative w-full aspect-video bg-white border-b-[3px] border-[var(--border-heavy)] overflow-hidden"
            data-testid="team-up-media"
          >
            <img
              src="/gifs/step-8.gif"
              alt="High-five celebration"
              className="w-full h-full object-contain"
              data-testid="team-up-gif"
            />
          </div>

          <div className="p-5 sm:p-6 flex flex-col gap-4">
            <p
              className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed"
              data-testid="team-up-body-1"
            >
              You have completed all of your tasks. Great job putting the kit
              together!
            </p>
            <p
              className="text-base sm:text-lg font-semibold text-[var(--text-main)] leading-relaxed"
              data-testid="team-up-body-2"
            >
              Now head over and help the Design Team finish theirs.
            </p>
          </div>
        </motion.article>
      </main>

      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
        data-testid="team-up-bottom-nav"
      >
        <button
          type="button"
          onClick={() => navigate("/final-packing")}
          aria-label="Back to final packing"
          data-testid="team-up-prev-btn"
          className="arcade-btn arcade-btn-icon"
        >
          <ChevronLeft strokeWidth={3} className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
